import React, { useEffect, useState } from 'react';
import { MessageSquare } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS (needs different handling)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Check if dismissed recently (within 24 hours)
    const dismissedTime = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissedTime) {
      const hoursSinceDismiss = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismiss < 24) {
        return;
      }
    }

    // For iOS, show custom prompt after delay
    if (isIOS && !navigator.standalone) {
      setTimeout(() => setShowPrompt(true), 1500);
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show prompt quickly - after 1.5 seconds
      setTimeout(() => setShowPrompt(true), 1500);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('✅ PWA instalado com sucesso!');
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!installPrompt) {
      // iOS fallback - show instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        alert('Para instalar:\n1. Toque no botão Compartilhar (ícone de seta)\n2. Selecione "Adicionar à Tela de Início"');
        return;
      }
      return;
    }

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('✅ Usuário aceitou instalar o PWA');
    }

    setInstallPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (isInstalled || !showPrompt) return null;

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 z-40 animate-fade-in"
        onClick={handleDismiss}
      />
      
      {/* Prompt Modal */}
      <div className="fixed bottom-0 left-0 right-0 md:bottom-4 md:left-auto md:right-4 md:w-96 bg-zinc-900 md:rounded-xl shadow-2xl border border-zinc-700 z-50 animate-slide-up overflow-hidden">
        {/* Header with logo */}
        <div className="bg-gradient-to-r from-emerald-600/20 to-green-600/20 p-4 flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl flex items-center justify-center shadow-md shadow-emerald-500/30">
            <MessageSquare className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white">
              MeuGestor
            </h3>
            <p className="text-xs text-zinc-400">
              Gestão e Cobrança via WhatsApp
            </p>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-zinc-300 mb-4">
            {isIOS 
              ? 'Adicione à tela inicial para acesso rápido e experiência completa!'
              : 'Instale o app para acesso rápido, notificações e funcionar offline!'
            }
          </p>
          
          {/* Benefits */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="flex flex-col items-center text-center p-2 bg-zinc-800/50 rounded-lg">
              <svg className="w-5 h-5 text-emerald-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-xs text-zinc-400">Rápido</span>
            </div>
            <div className="flex flex-col items-center text-center p-2 bg-zinc-800/50 rounded-lg">
              <svg className="w-5 h-5 text-blue-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="text-xs text-zinc-400">Notificações</span>
            </div>
            <div className="flex flex-col items-center text-center p-2 bg-zinc-800/50 rounded-lg">
              <svg className="w-5 h-5 text-green-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="text-xs text-zinc-400">Offline</span>
            </div>
          </div>
          
          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleInstallClick}
              className="flex-1 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-semibold px-4 py-3 rounded-lg hover:from-emerald-700 hover:to-green-700 transition-all shadow-lg"
              data-testid="pwa-install-btn"
            >
              {isIOS ? 'Como instalar' : 'Instalar agora'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-3 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
              data-testid="pwa-dismiss-btn"
            >
              Depois
            </button>
          </div>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
};

export default PWAInstallPrompt;
